import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Field, Form, Formik, ErrorMessage } from "formik";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import axios from "axios";
import { toast } from "react-toastify";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { RiLockPasswordLine } from "react-icons/ri";

import logo from "../assets/images/LogoMercadito.png";
import { getResetPassword, logout, resetPassword, resetPasswordUser } from "../slices/auth";
import { clearMessage, setMessage } from "../slices/message";
import { API_URL_AUTH } from "../constants/index";
import authHeader from "../services/auth-header";
import { State } from "../interfaces/types";
import { AppDispatch, RootState } from "../store";

interface ResetPasswordForm {
  email: string;
  password: string;
  confirm_password: string;
}

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { user: currentUser } = useSelector((state: State) => state.auth);
  const { isLoggedIn } = useSelector((state: RootState) => state.auth);
  const { message } = useSelector((state: RootState) => state.message);
  const { token } = useParams<{ token: string }>();

  const [successful, setSuccessful] = useState<boolean>(false);
  const [successfulToken, setSuccessfulToken] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");

  const updateFirstLogin = async (userId: number, firstLogin: boolean) => {
    try {
      const response = await axios.put(
        `${API_URL_AUTH}/users/${userId}/first-login`,
        { firstLogin },
        { headers: authHeader() }
      );
      console.log("Success:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("Error updating firstLogin:", error.response?.data || error.message);
      throw error;
    }
  };

  useEffect(() => {
    dispatch(clearMessage());

    if (token) {
      setLoading(true);

      dispatch(getResetPassword({ token }))
        .unwrap()
        .then((data: { email: string }) => {
          setEmail(data.email);
          setSuccessfulToken(true);
        })
        .catch(() => {
          setSuccessful(false);
          setSuccessfulToken(false);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [dispatch, token]);

  const initialValues: ResetPasswordForm = {
    email,
    password: "",
    confirm_password: "",
  };

  const handleGoToLogin = async () => {
    await dispatch(logout());
    navigate("/admin", { replace: true });
  };

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .min(6, "Contrase\u00f1a debe tener de 6 a 40 caracteres.")
      .max(40, "Contrase\u00f1a debe tener de 6 a 40 caracteres.")
      .required("\u00a1Contrase\u00f1a nueva requerida!"),
    confirm_password: Yup.string()
      .min(6, "Contrase\u00f1a debe tener de 6 a 40 caracteres.")
      .max(40, "Contrase\u00f1a debe tener de 6 a 40 caracteres.")
      .required("\u00a1Contrase\u00f1a de confirmaci\u00f3n requerida!"),
  });

  const validateConfirmPassword = (
    password: string,
    confirm_password: string
  ): boolean => password === confirm_password;

  const handleResetPassword = (formValue: ResetPasswordForm) => {
    const { email, password, confirm_password } = formValue;
    setLoading(true);
    setSuccessful(false);
    setSuccessfulToken(true);

    dispatch(clearMessage());

    if (validateConfirmPassword(password, confirm_password) && token) {
      if (!currentUser || currentUser?.firstLogin === false) {
        dispatch(resetPassword({ token, email, password }))
          .unwrap()
          .then(() => {
            setSuccessful(true);
          })
          .catch(() => {
            setSuccessful(false);
            setSuccessfulToken(false);
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        const username = currentUser?.username ?? "";

        dispatch(resetPasswordUser({ token, username, password }))
          .unwrap()
          .then(async () => {
            setSuccessful(true);
            const userId = Number(currentUser?.id ?? "");
            await updateFirstLogin(userId, false);
            toast.success("La contrase\u00f1a fue cambiada exitosamente");
            handleGoToLogin();
          })
          .catch(() => {
            setSuccessful(false);
            setSuccessfulToken(false);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    } else {
      setLoading(false);
      setSuccessful(false);
      setSuccessfulToken(true);
      dispatch(
        setMessage(
          "Error: \u00a1La contrase\u00f1a nueva y la contrase\u00f1a de confirmaci\u00f3n no coinciden!"
        )
      );
    }
  };

  if (isLoggedIn && currentUser?.firstLogin === false) {
    return <Navigate to="/" />;
  }

  return (
    <main className="main login-page">
      <section className="login-content">
        <div className="login-logo-container">
          <img src={logo} alt="Webb Logo" className="login-logo" />
        </div>

        <div className="login-card">
          <h1 className="login-title">{"Cambiar contrase\u00f1a"}</h1>
          <p className="login-subtitle">
            {currentUser?.firstLogin
              ? "La clave ingresada es temporal. Actualice su contrase\u00f1a para continuar"
              : "Ingrese y confirme su nueva contrase\u00f1a"}
          </p>

          {successfulToken ? (
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleResetPassword}
            >
              <Form className="login-form-container">
                <Field name="email" type="hidden" className="form-control" />

                <div className="login-field-group">
                  <div className="login-input-wrapper">
                    <RiLockPasswordLine className="login-input-icon" />
                    <Field
                      name="password"
                      type="password"
                      placeholder={"Nueva contrase\u00f1a"}
                      className="form-control login-input"
                      autoComplete="new-password"
                    />
                  </div>
                  <ErrorMessage
                    name="password"
                    component="div"
                    className="alert alert-danger login-error"
                  />
                </div>

                <div className="login-field-group">
                  <div className="login-input-wrapper">
                    <RiLockPasswordLine className="login-input-icon" />
                    <Field
                      name="confirm_password"
                      type="password"
                      placeholder={"Confirmar contrase\u00f1a"}
                      className="form-control login-input"
                      autoComplete="new-password"
                    />
                  </div>
                  <ErrorMessage
                    name="confirm_password"
                    component="div"
                    className="alert alert-danger login-error"
                  />
                </div>

                <button
                  type="submit"
                  className="btn login-submit-btn"
                  disabled={loading}
                >
                  {loading && (
                    <span className="spinner-border spinner-border-sm me-2" />
                  )}
                  {"Cambiar contrase\u00f1a"}
                </button>
              </Form>
            </Formik>
          ) : (
            <div className="reset-password-empty-state">
              <p className="reset-password-copy">
                {message}. Para generar un nuevo token utilice la opcion
                "Recuperar Contrase\u00f1a":
              </p>
              <Link to="/forgot-password" className="forgot-password-link">
                <IoArrowBackCircleOutline className="back-icon" />{" "}
                {"Recuperar Contrase\u00f1a"}
              </Link>
            </div>
          )}

          <button
            type="button"
            onClick={handleGoToLogin}
            className="forgot-password-link reset-password-action"
          >
            <IoArrowBackCircleOutline className="back-icon" />{" "}
            {"Iniciar Sesi\u00f3n"}
          </button>

          {message && (
            <div
              className={
                successful
                  ? "alert alert-success login-message"
                  : "alert alert-danger login-message"
              }
              role="alert"
            >
              {message}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default ResetPassword;
